/* ==================== AddEnquiry.jsx - API INTEGRATED ==================== */
import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import "./AddEnquiry.css";

function AddEnquiry({ handleCancel, fetchData, editingEnquiry, setFilteredAdmissions }) {
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get("public") === "true";

  // API BASE URL
  const API_BASE_URL = "https://aadyaacademy.org/api";

  const [newEnquiry, setNewEnquiry] = useState({
    enquiry_name: "",
    enquiry_aadhar: "",
    class: "",
    gender: "",
    dob: "",
    enquiry_blood_group: "",
    address: "",
    father_name: "",
    father_phone: "",
    father_email: "",
    father_occupation: "",
    father_aadhar: "",
    mother_name: "",
    mother_email: "",
    mother_phone: "",
    mother_occupation: "",
    mother_aadhar: "",
    enquiry_image: "",
    remarks: "First enquiry",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [classes] = useState([
    { id: 1, name: "1" },
    { id: 2, name: "2" },
    { id: 3, name: "3" },
    { id: 4, name: "4" },
    { id: 5, name: "5" },
    { id: 6, name: "6" },
    { id: 7, name: "7" },
    { id: 8, name: "8" },
    { id: 9, name: "9" },
    { id: 10, name: "10" },
  ]);

  const [showAdjust, setShowAdjust] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);

  const originalImageRef = useRef(null);
  const canvasRef = useRef(null);

  /* ========== PUBLIC MODE STYLING ========== */
  useEffect(() => {
    if (isPublic) {
      document.body.style.background = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
      document.querySelectorAll(".sidebar, .header, .navbar, nav").forEach((el) => {
        if (el) el.style.display = "none";
      });

      const container = document.querySelector(".add-enquiry-container");
      if (container) {
        container.style.maxWidth = "900px";
        container.style.margin = "20px auto";
        container.style.borderRadius = "25px";
        container.style.overflow = "hidden";
        container.style.boxShadow = "0 20px 60px rgba(0,0,0,0.4)";
        container.style.background = "white";
      }
    }
  }, [isPublic]);

  /* ========== EDIT MODE - FILL FORM ========== */
  useEffect(() => {
    if (editingEnquiry) {
      const normalizedGender =
        editingEnquiry.gender === "M"
          ? "male"
          : editingEnquiry.gender === "F"
          ? "female"
          : "";

      setNewEnquiry({
        ...editingEnquiry,
        gender: normalizedGender,
        dob: editingEnquiry.dob
          ? new Date(editingEnquiry.dob).toISOString().split("T")[0]
          : "",
      });

      if (editingEnquiry.enquiry_image) setImagePreview(editingEnquiry.enquiry_image);
    }
  }, [editingEnquiry]);

  /* ========== IMAGE LOGIC ========== */
  const resizeImage = (file, maxW = 800, maxH = 800) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxW) {
            height = (maxW * height) / width;
            width = maxW;
          }
          if (height > maxH) {
            width = (maxH * width) / height;
            height = maxH;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const processImageFile = async (file) => {
    if (!file) return;
    try {
      const resized = await resizeImage(file);
      setImagePreview(resized);
      originalImageRef.current = resized;
      setNewEnquiry((prev) => ({ ...prev, enquiry_image: resized }));
    } catch (err) {
      toast.error("Image processing failed");
    }
  };

  const applyAdjustments = () => {
    if (!originalImageRef.current) return;
    const img = new Image();
    img.onload = () => {
      const size = 300;
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.translate(size / 2, size / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);
      ctx.translate(-size / 2 + panX, -size / 2 + panY);
      ctx.drawImage(img, 0, 0, size, size);
      ctx.restore();
      const finalBase64 = canvas.toDataURL("image/jpeg", 0.8);
      setImagePreview(finalBase64);
      setNewEnquiry((prev) => ({ ...prev, enquiry_image: finalBase64 }));
      setShowAdjust(false);
    };
    img.src = originalImageRef.current;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEnquiry((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = () => {
    setNewEnquiry({
      enquiry_name: "",
      enquiry_aadhar: "",
      class: "",
      gender: "",
      dob: "",
      enquiry_blood_group: "",
      address: "",
      father_name: "",
      father_phone: "",
      father_email: "",
      father_occupation: "",
      father_aadhar: "",
      mother_name: "",
      mother_email: "",
      mother_phone: "",
      mother_occupation: "",
      mother_aadhar: "",
      enquiry_image: "",
      remarks: "First enquiry",
    });
    setImagePreview(null);
    setShowAdjust(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (showAdjust) applyAdjustments();

    const payload = {
      ...newEnquiry,
      gender: newEnquiry.gender === "male" ? "M" : newEnquiry.gender === "female" ? "F" : "",
    };

    try {
      const url = editingEnquiry
        ? `${API_BASE_URL}/enquiries/update/${editingEnquiry.enquiry_id}`
        : `${API_BASE_URL}/enquiries/add`;

      const method = editingEnquiry ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingEnquiry ? "Enquiry updated!" : "Submitted successfully!");
        if (isPublic) {
          setTimeout(() => { window.location.href = "https://aadyaacademy.org"; }, 3000);
        } else {
          await fetchData?.();
          handleCancel?.();
        }
      } else {
        const errData = await response.json();
        toast.error(errData.message || "Failed to save record");
      }
    } catch (err) {
      toast.error("Connection Error: Please check your internet or API.");
    }
  };

  return (
    <div className="add-enquiry-container">
      {isPublic && (
        <div className="public-header" style={{ textAlign: "center", padding: "30px 20px" }}>
          <img src="/logo.png" alt="Logo" style={{ width: "90px", borderRadius: "50%" }} />
          <h1 style={{ color: "#5a67d8" }}>Aadya Academy</h1>
        </div>
      )}

      <div className="add-enq-c">
        <h1 className="add-enquiry-title">
          {isPublic ? "Submit Your Details" : editingEnquiry ? "Edit Enquiry" : "Add New Enquiry"}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* IMAGE SECTION - SIDE BY SIDE ENABLED */}
          <div className="add-img">
            <div className="img-preview-wrapper">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="enquiry-preview-img" 
                  style={{ transform: showAdjust ? `scale(${zoom}) rotate(${rotation}deg)` : "none" }} 
                />
              ) : (
                <div className="no-img-placeholder">No image</div>
              )}
            </div>

            <div className="upload-controls">
              <button type="button" className="gallery-btn" onClick={() => document.getElementById("galleryInput").click()}>
                Gallery
              </button>
              <button type="button" className="camera-btn" onClick={() => document.getElementById("cameraInput").click()}>
                <FontAwesomeIcon icon={faCamera} /> Photo
              </button>
              {imagePreview && !showAdjust && (
                <button type="button" className="adjust-btn" onClick={() => setShowAdjust(true)}>
                  Adjust
                </button>
              )}
            </div>

            <input id="galleryInput" type="file" accept="image/*" hidden onChange={(e) => processImageFile(e.target.files[0])} />
            <input id="cameraInput" type="file" accept="image/*" capture="environment" hidden onChange={(e) => processImageFile(e.target.files[0])} />
          </div>

          {showAdjust && (
            <div className="image-adjust-panel">
              <label>Zoom: <input type="range" min="1" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} /></label>
              <button type="button" onClick={() => setRotation((r) => r + 90)}>Rotate 90°</button>
              <div className="adjust-actions">
                <button type="button" className="apply-btn" onClick={applyAdjustments}>Apply</button>
                <button type="button" className="cancel-btn" onClick={() => setShowAdjust(false)}>Close</button>
              </div>
            </div>
          )}

          {/* FORM FIELDS */}
          <div className="ad-f-n">
            <div className="field-wrapper">
              <h4>Enquiry Name</h4>
              <input type="text" name="enquiry_name" value={newEnquiry.enquiry_name} onChange={(e) => handleInputChange({ target: { name: "enquiry_name", value: e.target.value.replace(/\d/g, "") } })} required />
            </div>
            <div className="field-wrapper">
              <h4>Aadhar</h4>
              <input type="text" name="enquiry_aadhar" value={newEnquiry.enquiry_aadhar} onChange={(e) => /^\d{0,12}$/.test(e.target.value) && handleInputChange(e)} maxLength={12} />
            </div>
            <div className="field-wrapper">
              <h4>Class</h4>
              <select name="class" value={newEnquiry.class} onChange={handleInputChange} required>
                <option value="">Select</option>
                {classes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="c-g-d">
            <div className="field-wrapper">
              <h4>Gender</h4>
              <select name="gender" value={newEnquiry.gender} onChange={handleInputChange} required>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="field-wrapper">
              <h4>Date of Birth</h4>
              <input type="date" name="dob" value={newEnquiry.dob} onChange={handleInputChange} required />
            </div>
            <div className="field-wrapper">
              <h4>Blood Group</h4>
              <input type="text" name="enquiry_blood_group" value={newEnquiry.enquiry_blood_group} onChange={handleInputChange} placeholder="e.g. O+" />
            </div>
          </div>

          <div className="h-a">
            <div className="field-wrapper full">
              <h4>Address</h4>
              <textarea name="address" value={newEnquiry.address} onChange={handleInputChange} required />
            </div>
          </div>

          <div className="parent">
            <div className="parent-col">
              <h4>Father's Details</h4>
              <input type="text" name="father_name" placeholder="Name" value={newEnquiry.father_name} onChange={handleInputChange} />
              <input type="text" name="father_phone" placeholder="Phone" value={newEnquiry.father_phone} maxLength={10} onChange={(e) => /^\d{0,10}$/.test(e.target.value) && handleInputChange(e)} />
              <input type="email" name="father_email" placeholder="Email" value={newEnquiry.father_email} onChange={handleInputChange} />
              <input type="text" name="father_occupation" placeholder="Occupation" value={newEnquiry.father_occupation} onChange={handleInputChange} />
              <input type="text" name="father_aadhar" placeholder="Aadhar" value={newEnquiry.father_aadhar} maxLength={12} onChange={handleInputChange} />
            </div>
            <div className="parent-col">
              <h4>Mother's Details</h4>
              <input type="text" name="mother_name" placeholder="Name" value={newEnquiry.mother_name} onChange={handleInputChange} />
              <input type="text" name="mother_phone" placeholder="Phone" value={newEnquiry.mother_phone} maxLength={10} onChange={(e) => /^\d{0,10}$/.test(e.target.value) && handleInputChange(e)} />
              <input type="email" name="mother_email" placeholder="Email" value={newEnquiry.mother_email} onChange={handleInputChange} />
              <input type="text" name="mother_occupation" placeholder="Occupation" value={newEnquiry.mother_occupation} onChange={handleInputChange} />
              <input type="text" name="mother_aadhar" placeholder="Aadhar" value={newEnquiry.mother_aadhar} maxLength={12} onChange={handleInputChange} />
            </div>
          </div>

          <div className="add-enq-btn">
            <button className="add-enq-btn-submit" type="submit">{editingEnquiry ? "Update" : isPublic ? "Submit Enquiry" : "Add"}</button>
            {!isPublic && (
              <>
                <button className="add-enq-btn-clear" type="button" onClick={handleClear}>Clear</button>
                <button className="add-enq-btn-cancel" type="button" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </div>
        </form>
      </div>
      <ToastContainer position={isPublic ? "top-center" : "top-right"} />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

export default AddEnquiry;